import React, { useState, useEffect } from 'react'
import { useOnboardingStore } from '../../../stores/onboardingStore'
import { StripePaymentForm } from '../../../components/payment/StripePaymentForm'
import { paymentService } from '../../../services/paymentService-v2'
import { ServiceConfigUtils } from '../../../config/services.utils'
import { useErrorHandler } from '../../../utils/errors'
import {
  CheckCircle,
  CreditCard,
  Shield,
  AlertCircle,
  Loader,
  Clock,
  Mail,
  Info
} from 'lucide-react'

export const PaymentScreen: React.FC = () => {
  const { data, nextScreen, previousScreen, updateData } = useOnboardingStore()
  const { handleError } = useErrorHandler()
  
  // Get selected service details first
  const selectedService = data.selectedService
  const services = ServiceConfigUtils.getActiveServices()
  const serviceDetails = services.find(s => s.id === selectedService?.id)

  // State management
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [paymentSuccess, setPaymentSuccess] = useState(false)

  // Show payment form directly for all paid services on component mount (run once)
  useEffect(() => {
    if (selectedService) {
      setShowPaymentForm(true)
    }
  }, []) // Run only once on mount to prevent remounting payment form

  // Set up payment service token
  useEffect(() => {
    const token = localStorage.getItem('stratiqx_access_token')
    if (token) {
      paymentService.setAccessToken(token)
    }
  }, [])

  // Get tier badge configuration
  const getTierBadgeConfig = (tierLevel?: string) => {
    switch (tierLevel) {
      case 'quick':
        return { label: 'QUICK', bgClass: 'bg-gradient-to-r from-emerald-500 to-green-500' }
      case 'standard':
        return { label: 'STANDARD', bgClass: 'bg-gradient-to-r from-blue-500 to-cyan-500' }
      case 'comprehensive':
        return { label: 'COMPREHENSIVE', bgClass: 'bg-gradient-to-r from-purple-500 to-pink-500' }
      case 'enterprise':
        return { label: 'ENTERPRISE', bgClass: 'bg-gradient-to-r from-amber-500 to-orange-500' }
      default:
        return { label: 'STANDARD', bgClass: 'bg-gradient-to-r from-blue-500 to-cyan-500' }
    }
  }

  const tierConfig = getTierBadgeConfig(selectedService?.tierLevel)

  const handlePaymentSuccess = async (
    paymentIntentId: string,
    billingDetails?: any,
    paymentMethod?: any,
    actualAmount?: number
  ) => {
    try {
      console.log(`✅ Payment successful: ${paymentIntentId}`)
      console.log(`💰 Actual amount charged: $${actualAmount}`)

      // Save payment information to the store
      const paymentInfo = {
        paymentIntentId,
        paymentStatus: 'succeeded' as const,
        billingDetails: billingDetails ? {
          cardholderName: billingDetails.cardholderName,
          email: billingDetails.email,
          companyName: billingDetails.companyName
        } : undefined,
        paymentMethod: paymentMethod ? {
          last4: paymentMethod.last4,
          brand: paymentMethod.brand
        } : undefined,
        amount: actualAmount !== undefined ? actualAmount : getPaymentAmount(), // Use actual amount if available
        currency: 'usd',
        processedAt: new Date().toISOString()
      }
      
      // Update the onboarding store with payment info
      updateData('paymentInfo', paymentInfo)
      console.log('💾 Payment info saved to store:', paymentInfo)
      
      setPaymentSuccess(true)
      setShowPaymentForm(false)
      
      // Navigate to next screen after success animation
      setTimeout(() => {
        nextScreen()
      }, 2000)
      
    } catch (error) {
      handleError(error)
    }
  }

  const handlePaymentError = (error: string) => {
    console.error(`❌ Payment error: ${error}`)
    setPaymentError(error)
    setShowPaymentForm(false)
  }

  const handlePaymentCancel = () => {
    console.log('🚫 Payment cancelled by user')
    setShowPaymentForm(false)
    setPaymentError(null)
  }

  const getPaymentAmount = () => {
    if (!selectedService?.id) return 0
    const paymentDetails = paymentService.getPaymentDetailsForService(selectedService.id)
    return paymentDetails.amount
  }

  // If no service selected, show error
  if (!selectedService || !serviceDetails) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Service Selected</h2>
          <p className="text-gray-600 mb-6">
            Please go back and select an intelligence service to continue.
          </p>
          <Button onClick={previousScreen} variant="outline">
            Go Back to Service Selection
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Complete Your Payment
        </h2>
        <p className="text-gray-600">
          Complete your secure payment to activate your intelligence service
        </p>
      </div>

      {/* Payment Success Display */}
      {paymentSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-green-900 mb-2">
              Payment Successful!
            </h3>
            <p className="text-green-800">
              Your payment has been processed successfully. Your service is now active!
            </p>
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-green-700">
              <Loader className="w-4 h-4 animate-spin" />
              Proceeding to final confirmation...
            </div>
        </div>
      )}

      {/* Service Summary - Subtle styling matching other screens */}
      {!paymentSuccess && (
        <section className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-3 text-lg font-semibold text-gray-900">
              <CheckCircle className="w-6 h-6 text-blue-600" />
              Selected Service
            </h3>
            <div className={`${tierConfig.bgClass} text-white rounded-full px-3 py-1 text-xs font-bold`}>
              {tierConfig.label}
            </div>
          </div>
          <div>
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-lg text-blue-900 mb-1">
                    {serviceDetails.name}
                  </h3>
                  <p className="text-blue-800 text-sm mb-3">
                    {serviceDetails.description}
                  </p>
                  <div className="flex items-center gap-4">
                    <span className="text-2xl font-bold text-blue-900">
                      {serviceDetails.price.display}
                    </span>
                  </div>
                </div>
              </div>

              {/* Service Features */}
              <div className="border-t border-blue-200 pt-4">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  What You'll Receive
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-blue-800">
                    <Clock className="h-4 w-4 text-blue-600" />
                    <span>Delivered within {serviceDetails.metadata.deliveryTime}</span>
                  </div>
                  <div className="flex items-center gap-2 text-blue-800">
                    <Mail className="h-4 w-4 text-blue-600" />
                    <span>Sent to {data.contactInfo.email}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Stripe Payment Form */}
      {showPaymentForm && (
        <section className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-600" />
            Payment Details
          </h3>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <StripePaymentForm
              amount={getPaymentAmount()}
              serviceType={selectedService.id}
              serviceTitle={serviceDetails.name}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              onCancel={handlePaymentCancel}
            />
          </div>

          {/* Security Notice - More subtle */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-gray-600 mt-0.5" />
              <div className="text-sm text-gray-600">
                <strong className="text-gray-700">Secure Payment:</strong> Your payment information is processed 
                through PCI-compliant systems. We never store your card details.
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Navigation handled by OnboardingLayout - removed custom navigation */}
    </div>
  )
}